import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { LOW_PASS_FILTER_CUTOFF } from '@/toolSelection/investigate.constants';
import { nextSignalToAggregate } from '@/tools/frequencyAnalysis/fft.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState } from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type { BaseToolPersistedState, ParameterDefinitions, ToolItem, ToolRehydratePayload } from '@/tools/tool.types';

type DurationLike = Partial<FrontendDuration>;

export interface FftState extends BaseToolPersistedState {
  /** The signal the FFT is computed for, or `undefined` when none is selected. */
  signalToAggregate?: ToolItem | null;
  /** When `true` the sampling rate/cutoffs are expressed as periods; when `false`, as frequencies. */
  rateType: boolean;
  /** The sampling rate/period used to compute the FFT. */
  rate: DurationLike;
  /** The units used to format the frequency bucket labels. */
  outputUnits: string;
  /** The high-pass filter cutoff applied to the FFT output. */
  highPass: DurationLike;
  /** The low-pass filter cutoff applied to the FFT output. */
  lowPass: DurationLike;
  /** When `true` the high-pass filter is applied. */
  useHighPass: boolean;
  /** When `true` the low-pass filter is applied. */
  useLowPass: boolean;
}

export type FftDehydratedState = FftState;

export interface FftActions {
  /** Sets whether the rate/cutoffs are expressed as periods (`true`) or frequencies (`false`). */
  setRateType: (rateType: boolean) => void;
  /** Sets the sampling rate. */
  setRate: (rate: DurationLike) => void;
  /** Sets the units used to format the frequency bucket labels. */
  setOutputUnits: (outputUnits: string) => void;
  /** Sets the high-pass filter cutoff. */
  setHighPass: (highPass: DurationLike) => void;
  /** Sets the low-pass filter cutoff. */
  setLowPass: (lowPass: DurationLike) => void;
  /** Sets whether the high-pass filter is applied. */
  setUseHighPass: (useHighPass: boolean) => void;
  /** Sets whether the low-pass filter is applied. */
  setUseLowPass: (useLowPass: boolean) => void;
  /** Sets the signal the FFT is computed for. Accepts an item, an object with an id, or a plain id string. */
  setSignalToAggregate: (item: ToolItem | { id: string } | string | null | undefined) => void;
  /** Sets the user-visible name of the output item. */
  setSearchName: (name: string) => void;
  /** Rehydrates the store from a persisted tool-for-edit payload. */
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  /** Resets the store to its initial state. */
  reset: () => void;
}

const parameterDefinitions: ParameterDefinitions = {
  signalToAggregate: { predicate: ['name', 'signalToAggregate'] },
};

const initialState: FftState = {
  ...BASE_TOOL_COMMON_PROPS,
  signalToAggregate: undefined,
  rateType: true,
  rate: LOW_PASS_FILTER_CUTOFF,
  // The minutes duration unit's primary label. Hard-coded (rather than derived via `getDurationTimeUnit`) so this
  // store file does not import `dateTime.utilities`, which transitively imports every store.
  outputUnits: 'min',
  highPass: LOW_PASS_FILTER_CUTOFF,
  lowPass: LOW_PASS_FILTER_CUTOFF,
  useHighPass: false,
  useLowPass: false,
};

export const dehydrateFftState = (state: FftState): FftDehydratedState => state;

const fftActionsFactory: ActionsFactory<FftState, FftActions> = (set, _get, resetState) => ({
  setRateType: (rateType) =>
    set((draft) => {
      draft.rateType = rateType;
    }),

  setRate: (rate) =>
    set((draft) => {
      draft.rate = rate;
    }),

  setOutputUnits: (outputUnits) =>
    set((draft) => {
      draft.outputUnits = outputUnits;
    }),

  setHighPass: (highPass) =>
    set((draft) => {
      draft.highPass = highPass;
    }),

  setLowPass: (lowPass) =>
    set((draft) => {
      draft.lowPass = lowPass;
    }),

  setUseHighPass: (useHighPass) =>
    set((draft) => {
      draft.useHighPass = useHighPass;
    }),

  setUseLowPass: (useLowPass) =>
    set((draft) => {
      draft.useLowPass = useLowPass;
    }),

  setSignalToAggregate: (item) =>
    set((draft) => {
      draft.signalToAggregate = nextSignalToAggregate(draft.signalToAggregate, item);
    }),

  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  rehydrateForEdit: (payload) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });
      // Merge `initialState` first to reset the store, then overlay the rehydrated values — this is the equivalent
      // of calling `resetState()` but in a single `set()` so subscribers only receive one update.
      Object.assign(draft, initialState, rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });
    }),

  reset: resetState,
});

const STORE_NAME = 'Fft';

const [FftStoreProvider, useFftStoreInstance, useFftSelector] = createStore(
  STORE_NAME,
  initialState,
  fftActionsFactory,
);

export { FftStoreProvider };

/** The signal the FFT is computed for. */
export const useFftSignalToAggregate = () => useFftSelector((state) => state.signalToAggregate);
/** Whether the rate/cutoffs are expressed as periods (`true`) or frequencies (`false`). */
export const useFftRateType = () => useFftSelector((state) => state.rateType);
/** The sampling rate/period used to compute the FFT. */
export const useFftRate = () => useFftSelector((state) => state.rate);
/** The units used to format the frequency bucket labels. */
export const useFftOutputUnits = () => useFftSelector((state) => state.outputUnits);
/** The high-pass filter cutoff. */
export const useFftHighPass = () => useFftSelector((state) => state.highPass);
/** The low-pass filter cutoff. */
export const useFftLowPass = () => useFftSelector((state) => state.lowPass);
/** Whether the high-pass filter is applied. */
export const useFftUseHighPass = () => useFftSelector((state) => state.useHighPass);
/** Whether the low-pass filter is applied. */
export const useFftUseLowPass = () => useFftSelector((state) => state.useLowPass);
/** The unique ID of the FFT output item. */
export const useFftId = () => useFftSelector((state) => state.id);
/** The user-visible name of the FFT output item. */
export const useFftName = () => useFftSelector((state) => state.name);

/** Returns the FFT store actions for updating state. */
export const useFftActions = () => useFftStoreInstance().actions;

/**
 * @deprecated For legacy singleton bridge only. Non-React consumers (action files) use the singleton
 * created from this in core.stores.ts. DO NOT use in new code.
 */
export function legacyCreateFftStore() {
  return createStoreInstance<FftState, FftActions>(initialState, fftActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistFftStore = () => {
  const register = useRegisterPersistedStore();
  const store = useFftStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqFftStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateFftState,
    });
  }, [register, store]);
};
